import { AfterViewInit, Component, ElementRef, EventEmitter, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { UserService } from '@services/user.context/user.context.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { JwtHelperService } from '@auth0/angular-jwt';

const GOOGLE_CLIENT_ID = '529714753402-4qi6se2533nuabibqt3mu1dro630e5m4.apps.googleusercontent.com';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit {
  isLocalhost = false;

  signinError = '';

  @ViewChild('googleButton')
  googleButton: ElementRef<HTMLElement>;

  /**
   *
   */
  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,

    private jwtHelper: JwtHelperService,
    private _ngZone: NgZone
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.user.subscribe(async ($event) => {
      this._ngZone.run(async () => { });
    });
  }

  ngOnInit(): void {
    this.isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    // Kept for backwards compatibility with any external caller of the old global callback.
    (window as any).userLoggedIn = (gResponse) => this.handleCredentialResponse(gResponse);
  }

  ngAfterViewInit(): void {
    this.initGoogleSignIn().catch((error) => {
      console.error(error);
      this._ngZone.run(() => {
        this.signinError = 'Could not load Google Sign-In. Please disable blockers for this site and reload.';
      });
    });
  }

  /**
   * The GSI script is loaded async/defer, so it may become available before or after this
   * view renders. Wait for it, then initialize and render an explicit button - the declarative
   * `g_id_onload` One Tap flow no longer renders on its own in current Chrome (FedCM).
   */
  private async initGoogleSignIn(): Promise<void> {
    const google = await this.waitForGoogle();

    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => this.handleCredentialResponse(response),
      auto_select: false,
      itp_support: true,
      use_fedcm_for_prompt: true,
      ux_mode: 'popup',
      context: 'signin',
    });

    google.accounts.id.renderButton(this.googleButton.nativeElement, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 280,
    });

    // Still offer One Tap on top of the button; harmless when it is suppressed.
    google.accounts.id.prompt();
  }

  private waitForGoogle(timeoutMs = 10000): Promise<any> {
    const started = performance.now();
    return new Promise((resolve, reject) => {
      const poll = () => {
        const google = (window as any).google;
        if (google?.accounts?.id) {
          resolve(google);
          return;
        }
        if (performance.now() - started > timeoutMs) {
          reject(new Error('Google Identity Services script failed to load'));
          return;
        }
        setTimeout(poll, 100);
      };
      poll();
    });
  }

  private handleCredentialResponse(gResponse: { credential: string }): void {
    this._ngZone.run(() => {
      const user = this.jwtHelper.decodeToken(gResponse.credential);
      this.authService
        .token({ email: user.email, idToken: gResponse.credential, authorizationCode: user.email_verified })
        .subscribe({
          next: (result) => {
            if (result.token) {
              this.userService.setToken(result.token);
              this.userService.setUser(result.user);
              this.router.navigateByUrl(this.resolveReturnUrl()).catch((error) => console.error(error));
            } else {
              this.signinError = 'Sign-in failed. Your account may not have admin access.';
            }
          },
          error: (error) => {
            console.error(error);
            this.signinError = 'Sign-in failed. Please try again.';
          },
        });
    });
  }

  /**
   * Where to land after a successful sign-in. Screens that need the operator to
   * authenticate first (the CLI approval screen, for one) send them here with a
   * `returnUrl`, and losing it would strand them on the dashboard.
   *
   * Only same-origin paths are honoured. A value starting with `//` is a
   * protocol-relative URL — `//evil.example` — so accepting one would turn this
   * into an open redirect off the back of a login.
   */
  private resolveReturnUrl(): string {
    const requested = this.route.snapshot.queryParamMap.get('returnUrl') ?? '';
    if (requested.startsWith('/') && !requested.startsWith('//')) {
      return requested;
    }
    return '/dashboard';
  }

  @Output()
  public userData: any;

  @Output()
  public user = new EventEmitter();

  rememberMe: boolean;

  userName: string;

  password: string;

  public async;

  scale(element, clsname) {
    element.className = `scaler-${clsname}`;
  }

  overrideLogin() {
    this.userName = 'roi@evinature.com';
    this.tryLogin();
  }

  async tryLogin() {
    this.authService.token({ email: this.userName, password: this.password }).subscribe((result) => {
      if (result.token) {
        this.userService.setToken(result.token);
        this.userService.setUser(result.user);
        this.router.navigateByUrl(this.resolveReturnUrl());
      }
    });
  }
}
